import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, List, Skeleton, Typography } from 'antd';
import { useQuery } from '@apollo/client';
import { GET_PATIENTS } from 'gql/patients/queries';
import InfiniteScroll from 'react-infinite-scroll-component';
import { PreviousPatients } from 'gql/patients/__generated__/PreviousPatients';
import { calculateAge } from '@medicusunion/front-utils';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { GENDERS_VALUES } from 'constants/profile';
import { UserOutlined } from '@ant-design/icons';

import { StyledPatientsCard } from './styled';
import { getFileUrl } from 'utils/file';

const { Title } = Typography;

const PAGE_LIMIT = 10;

interface IProps {
  onReady(): void;
}

const PatientList: FC<IProps> = ({ onReady }) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const page = useRef(0);
  const timer = useRef<NodeJS.Timeout>();
  const [search, setSearch] = useState('');
  const { data, loading, fetchMore } = useQuery<PreviousPatients>(
    GET_PATIENTS,
    {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      variables: {
        query: {
          name: search,
          limit: PAGE_LIMIT,
          page: 0
        }
      },
      onCompleted() {
        setTimeout(() => {
          onReady();
        });
      }
    }
  );

  const onSearch = (value: string) => {
    page.current = 0;
    clearTimeout(timer.current);

    timer.current = setTimeout(() => {
      setSearch(value);
    }, 350);
  };

  useEffect(
    () => () => {
      clearTimeout(timer.current);
    },
    []
  );

  useEffect(() => {
    if (!id && data?.previousPatients?.data.results?.[0]?.id) {
      navigate(data?.previousPatients.data.results[0].id);
    }
  }, [data?.previousPatients?.data.results, id, navigate]);

  const loadMoreData = () => {
    page.current = page.current + 1;

    fetchMore({
      variables: {
        query: {
          name: search,
          page: page.current,
          limit: PAGE_LIMIT
        }
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const newResult = fetchMoreResult.previousPatients?.data.results || [];

        if (!previousResult.previousPatients?.data.results || !newResult) {
          return previousResult;
        }

        return {
          previousPatients: {
            ...previousResult.previousPatients,
            data: {
              ...previousResult.previousPatients.data,
              results: [
                ...previousResult.previousPatients.data.results,
                ...newResult
              ]
            }
          }
        };
      }
    });
  };

  const results = data?.previousPatients?.data.results || [];
  const total = data?.previousPatients?.data.total || 0;

  return (
    <StyledPatientsCard id="scrollableDiv">
      <InfiniteScroll
        dataLength={results.length}
        next={loadMoreData}
        hasMore={results.length < total}
        loader={
          <Skeleton
            avatar
            paragraph={{ rows: 1 }}
            active
            style={{ padding: 24 }}
          />
        }
        scrollableTarget="scrollableDiv"
      >
        <List
          itemLayout="horizontal"
          dataSource={results}
          loading={loading}
          header={
            <div className="patients-header">
              <Title level={5}>{t('patient.all_patients')}</Title>
              <div>
                <Input
                  size="large"
                  placeholder={t('common.search_by_name')}
                  onChange={e => onSearch(e.target.value)}
                />
              </div>
            </div>
          }
          renderItem={item => {
            const ageStr = item.date_of_birth
              ? t('patient.years', {
                  num: calculateAge(item.date_of_birth)
                })
              : '';

            const genderStr = item.gender
              ? `, ${t(`patient.${GENDERS_VALUES[item.gender]}`)}`
              : null;

            if (!item.id) {
              return null;
            }

            return (
              <Link to={`/patients/${item.id}`} key={item.id}>
                <List.Item className={id === item.id ? 'active-list-item' : ''}>
                  <List.Item.Meta
                    avatar={
                      item.avatar ? (
                        <img
                          src={getFileUrl(item.avatar, '', 150)}
                          className="list-item-avatar"
                          alt={item.first_name || ''}
                        />
                      ) : (
                        <div className="list-item-avatar">
                          <UserOutlined
                            style={{ fontSize: 18, color: '#ffffff' }}
                          />
                        </div>
                      )
                    }
                    title={`${item.first_name} ${item.last_name}`}
                    description={
                      <p className="list-item-text">
                        {ageStr}
                        {genderStr}
                        <br />#{item.id}
                      </p>
                    }
                  />
                </List.Item>
              </Link>
            );
          }}
        />
      </InfiniteScroll>
    </StyledPatientsCard>
  );
};

export default PatientList;
